// API-backed computation for candidates: friend commute times (Distance
// Matrix), trip drive/transit times, and nearest-amenity walk times.
//
// Core functions take a {lat,lng} origin and RETURN computed data (so the
// suggestion enricher can score ad-hoc points). Thin `*ForCandidate(id)`
// wrappers write the result back into the candidates store.

import { get } from 'svelte/store';
import { candidates, trips, friends, updateCandidate, amenityPrefs } from './state';
import { AMENITY_BY_KEY, NAME_MATCHERS, activeAmenities, resolveAmenityKeys } from './scoring';
import type { Candidate, CommuteLeg, LatLng, TravelMode, TripLegData } from './types';
import { placesNearby, osmNearby, type OsmPlace } from './api';

const MODES: TravelMode[] = ['DRIVING', 'TRANSIT', 'BICYCLING', 'WALKING'];

/** Next Friday 6pm — realistic "going to see friends" timing for traffic. */
function nextWeekdayMorning(): Date {
  const d = new Date();
  const day = d.getDay();
  const daysUntilFriday = (5 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilFriday);
  d.setHours(18, 0, 0, 0);
  return d;
}

function currentCandidate(id: string): Candidate | undefined {
  return get(candidates).find((c) => c.id === id);
}

// ---- core (return values) ----
async function computeMode(origin: LatLng, mode: TravelMode): Promise<CommuteLeg[]> {
  const fr = get(friends);
  if (!fr.length) return [];
  const params = new URLSearchParams({
    origins: `${origin.lat},${origin.lng}`,
    destinations: fr.map((f) => `${f.lat},${f.lng}`).join('|'),
    mode: mode.toLowerCase(),
    units: 'imperial',
  });
  if (mode === 'DRIVING' || mode === 'TRANSIT') {
    params.set('departure_time', String(Math.floor(nextWeekdayMorning().getTime() / 1000)));
  }
  if (mode === 'DRIVING') params.set('traffic_model', 'best_guess');

  try {
    const resp = await fetch(`/api/distance-matrix?${params}`);
    if (!resp.ok) return fr.map((f) => ({ name: f.name, minutes: null }));
    const data = await resp.json();
    if (data.status !== 'OK') return fr.map((f) => ({ name: f.name, minutes: null }));
    return data.rows[0].elements.map((el: any, i: number) => {
      if (el.status !== 'OK') return { name: fr[i].name, minutes: null };
      const seconds = (el.duration_in_traffic || el.duration).value;
      return { name: fr[i].name, minutes: Math.round(seconds / 60), text: el.distance?.text };
    });
  } catch {
    return fr.map((f) => ({ name: f.name, minutes: null }));
  }
}

export async function computeModes(origin: LatLng): Promise<Candidate['results']> {
  const results: Candidate['results'] = {};
  for (const mode of MODES) results[mode] = await computeMode(origin, mode);
  return results;
}

export async function computeTrips(origin: LatLng): Promise<Record<string, TripLegData>> {
  const tr = get(trips);
  if (!tr.length) return {};
  const dests = tr.map((t) => ({ lat: t.lat, lng: t.lng }));

  const fetchMode = async (mode: 'DRIVING' | 'TRANSIT') => {
    const params = new URLSearchParams({
      origins: `${origin.lat},${origin.lng}`,
      destinations: dests.map((d) => `${d.lat},${d.lng}`).join('|'),
      mode: mode.toLowerCase(),
      units: 'imperial',
      departure_time: String(Math.floor(nextWeekdayMorning().getTime() / 1000)),
    });
    if (mode === 'DRIVING') params.set('traffic_model', 'best_guess');
    try {
      const resp = await fetch(`/api/distance-matrix?${params}`);
      if (!resp.ok) return tr.map(() => ({ minutes: null }));
      const data = await resp.json();
      if (data.status !== 'OK') return tr.map(() => ({ minutes: null }));
      return tr.map((_, i) => {
        const el = data.rows[0].elements[i];
        if (el.status !== 'OK') return { minutes: null };
        const seconds = (el.duration_in_traffic || el.duration).value;
        return { minutes: Math.round(seconds / 60) };
      });
    } catch {
      return tr.map(() => ({ minutes: null }));
    }
  };

  const [drives, transits] = await Promise.all([fetchMode('DRIVING'), fetchMode('TRANSIT')]);
  const tripData: Record<string, TripLegData> = {};
  tr.forEach((t, i) => {
    tripData[t.id] = { driving: drives[i], transit: transits[i] };
  });
  return tripData;
}

interface NearbyPlace { name: string; placeId: string; lat: number; lng: number; }

async function findNearbyPlaces(center: LatLng, type: string): Promise<NearbyPlace[]> {
  const results = await placesNearby(center, type);
  return results
    .filter((r: any) => r.business_status !== 'CLOSED_PERMANENTLY')
    .map((r: any) => ({
      name: r.name,
      placeId: r.place_id,
      lat: r.geometry.location.lat,
      lng: r.geometry.location.lng,
    }));
}

async function walkingTimes(origin: LatLng, dests: LatLng[]): Promise<({ minutes: number } | null)[]> {
  if (!dests.length) return [];
  const params = new URLSearchParams({
    origins: `${origin.lat},${origin.lng}`,
    destinations: dests.map((d) => `${d.lat},${d.lng}`).join('|'),
    mode: 'walking',
    units: 'imperial',
  });
  try {
    const resp = await fetch(`/api/distance-matrix?${params}`);
    if (!resp.ok) return dests.map(() => null);
    const data = await resp.json();
    if (data.status !== 'OK') return dests.map(() => null);
    return data.rows[0].elements.map((el: any) =>
      el.status === 'OK' ? { minutes: Math.round(el.duration.value / 60) } : null,
    );
  } catch {
    return dests.map(() => null);
  }
}

const OSM_RADIUS_M = 3000;

export interface ComputeAmenityOptions {
  /** Which amenity keys to look up. Defaults to the user's current picks. */
  enabled?: Set<string>;
  /** Only fetch keys missing from `prior` (used when the user enables new picks). */
  onlyMissing?: boolean;
}

/**
 * Nearest-of-each amenity with walking minutes. Daily amenities come from
 * Google Places, parks/recreation from OSM. Results for keys that are not
 * enabled are left untouched in `prior` (so re-enabling is free); keys no
 * longer defined in AMENITIES are dropped.
 */
export async function computeAmenities(
  origin: LatLng,
  prior: Candidate['amenities'] = null,
  opts: ComputeAmenityOptions = {},
): Promise<NonNullable<Candidate['amenities']>> {
  const amenities: NonNullable<Candidate['amenities']> = { ...(prior || {}) };
  for (const k of Object.keys(amenities)) if (!(k in AMENITY_BY_KEY)) delete amenities[k];

  const enabled = opts.enabled ?? resolveAmenityKeys(get(amenityPrefs).enabled);
  const wanted = activeAmenities(enabled).filter((a) => !(opts.onlyMissing && a.key in amenities));
  if (!wanted.length) return amenities;

  const googleTypes = [...new Set(wanted.filter((a) => a.source === 'google').map((a) => a.placesType!))];
  const osmCats = [...new Set(wanted.filter((a) => a.source === 'osm').map((a) => a.osmCategory!))];

  const placesByType: Record<string, NearbyPlace[]> = {};
  const [, osm] = await Promise.all([
    Promise.all(googleTypes.map(async (type) => { placesByType[type] = await findNearbyPlaces(origin, type); })),
    osmCats.length ? osmNearby(origin, osmCats, OSM_RADIUS_M) : Promise.resolve({} as Record<string, OsmPlace[]>),
  ]);

  const dests: LatLng[] = [];
  const picks: { key: string; place: NearbyPlace; areaSqm?: number }[] = [];
  for (const a of wanted) {
    let picked: NearbyPlace | null = null;
    let areaSqm: number | undefined;
    let errorMsg: string | null = null;

    if (a.source === 'google') {
      const all = placesByType[a.placesType!] || [];
      if (all.length === 0) {
        errorMsg = 'none nearby';
      } else if (a.match) {
        // Prefer a recognized major chain, but fall back to the nearest result
        // (Google ranks by prominence) when nothing matches — our name lists
        // are US/SF-centric, so the fallback is what makes scoring work in
        // cities anywhere in the world.
        const matcher = NAME_MATCHERS[a.match];
        picked = (matcher ? all.find((p) => matcher(p.name)) : null) || all[0];
      } else {
        picked = all[0];
      }
    } else if (osm === null) {
      // Overpass down/rate-limited: leave any prior value, otherwise mark
      // transient so the reconcile-on-load pass retries next time.
      if (!(a.key in amenities)) amenities[a.key] = { error: 'lookup unavailable' };
      continue;
    } else {
      const all = osm[a.osmCategory!] || [];
      const min = a.minAreaSqm ?? 0;
      const hit = all.find((p) => (min ? (p.areaSqm ?? 0) >= min : true));
      if (hit) {
        picked = { name: hit.name, placeId: hit.id, lat: hit.lat, lng: hit.lng };
        areaSqm = hit.areaSqm;
      } else {
        errorMsg = all.length ? `none this size within ${OSM_RADIUS_M / 1000} km` : `none within ${OSM_RADIUS_M / 1000} km`;
      }
    }

    if (picked) {
      dests.push({ lat: picked.lat, lng: picked.lng });
      picks.push({ key: a.key, place: picked, areaSqm });
    } else {
      amenities[a.key] = { error: errorMsg || true };
    }
  }

  // Distance Matrix caps destinations at 25 per request.
  const walks: ({ minutes: number } | null)[] = [];
  for (let i = 0; i < dests.length; i += 25) {
    walks.push(...(await walkingTimes(origin, dests.slice(i, i + 25))));
  }
  picks.forEach((entry, i) => {
    amenities[entry.key] = {
      name: entry.place.name,
      minutes: walks[i]?.minutes ?? null,
      ...(entry.areaSqm != null ? { areaSqm: entry.areaSqm } : {}),
    };
  });
  return amenities;
}

// ---- store-writing wrappers (by id) ----
export async function computeAllModesForCandidate(id: string): Promise<void> {
  const c = currentCandidate(id);
  if (!c) return;
  updateCandidate(id, { results: await computeModes({ lat: c.lat, lng: c.lng }) });
}

export async function computeTripsForCandidate(id: string): Promise<void> {
  const c = currentCandidate(id);
  if (!c) return;
  updateCandidate(id, { trips: await computeTrips({ lat: c.lat, lng: c.lng }) });
}

export async function findAmenitiesForCandidate(id: string, opts: ComputeAmenityOptions = {}): Promise<void> {
  const c = currentCandidate(id);
  if (!c) return;
  const amenities = await computeAmenities({ lat: c.lat, lng: c.lng }, c.amenities, opts);
  // The candidate may have moved/been removed while we were fetching.
  if (currentCandidate(id)) updateCandidate(id, { amenities });
}

/** Fill in amenities the user just enabled, for every candidate, without refetching the rest. */
export function fillMissingAmenitiesForAll(): void {
  for (const c of get(candidates)) findAmenitiesForCandidate(c.id, { onlyMissing: true });
}

/** Full enrichment for one candidate (commute + amenities + trips). */
export function enrichCandidate(id: string): void {
  computeAllModesForCandidate(id);
  findAmenitiesForCandidate(id);
  computeTripsForCandidate(id);
}

/** A friend changed → every candidate's commute is stale. */
export async function recomputeCandidatesForFriendChange(): Promise<void> {
  for (const c of get(candidates)) await computeAllModesForCandidate(c.id);
}

/** A trip changed → every candidate's trip times are stale. */
export async function recomputeCandidatesForTripChange(): Promise<void> {
  for (const c of get(candidates)) await computeTripsForCandidate(c.id);
}
